// client/src/hooks/useVouchers.js
// TanStack Query hooks for the voucher resource. Mirrors the pattern
// the other resources use (useCamps, useStudents, ...) so the
// Vouchers tab is mostly composition.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

const listKey  = (campId, query) => ['camps', campId, 'vouchers', query];
const statsKey = (campId)        => ['camps', campId, 'vouchers', 'stats'];

export function useVouchers(campId, query = {}) {
  return useQuery({
    queryKey: listKey(campId, query),
    queryFn: () => api.get(`/api/camps/${campId}/vouchers`, { query }),
    enabled: !!campId,
    placeholderData: (prev) => prev,
  });
}

export function useVoucherStats(campId) {
  return useQuery({
    queryKey: statsKey(campId),
    queryFn: () => api.get(`/api/camps/${campId}/vouchers/stats`),
    enabled: !!campId,
  });
}

const batchesKey = (campId) => ['camps', campId, 'vouchers', 'batches'];

export function useVoucherBatches(campId, { enabled = true } = {}) {
  return useQuery({
    queryKey: batchesKey(campId),
    queryFn: () => api.get(`/api/camps/${campId}/vouchers/batches`),
    enabled: !!campId && enabled,
  });
}

function invalidate(qc, campId) {
  qc.invalidateQueries({ queryKey: ['camps', campId, 'vouchers'] });
}

export function useCreateVoucherBatch(campId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`/api/camps/${campId}/vouchers`, body),
    onSuccess: () => invalidate(qc, campId),
  });
}

export function useCancelVoucher(campId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ voucherId, reason }) =>
      api.post(`/api/camps/${campId}/vouchers/${voucherId}/cancel`, { reason }),
    onSuccess: () => invalidate(qc, campId),
  });
}

export function useCancelVoucherBatch(campId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ batchLabel, createdAt, reason }) =>
      api.post(`/api/camps/${campId}/vouchers/cancel-batch`, { batchLabel, createdAt, reason }),
    onSuccess: () => invalidate(qc, campId),
  });
}
